import React, { useState, useEffect, useCallback } from 'react';
import { View, ScrollView, Alert, Dimensions, RefreshControl } from 'react-native';
import { YStack, XStack, H2, H6, H3, H4, SizableText, Card, Button, Separator, Spinner } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import DefaultScreen from '@components/Global/DefaultScreenView';
import CustomDialog from '@components/UI/CustomDialog';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import apis from '@api/api';

interface CompanyInfo {
  _id: string;
  company_name: string;
  location?: string;
  departments?: Array<{
    _id: string;
    name: string;
    description?: string;
  }>;
  userCount?: number;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  departmentStats: Array<{
    departmentId: string;
    departmentName: string;
    userCount: number;
  }>;
}

const CompanySettings: React.FC = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const companyId = currentCompany?._id;

  // Debug: Log currentCompany structure
  useEffect(() => {
    console.log('🏢 CompanySettings - currentCompany:', {
      hasCurrentCompany: !!currentCompany,
      companyId: currentCompany?._id,
      companyIdAlt: currentCompany?.id,
      fullCompany: currentCompany
    });
  }, [currentCompany]);

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [companyList, setCompanyList] = useState<CompanyInfo[]>([]);
  const [departments, setDepartments] = useState<Array<any>>([]);
  const [selectedSection, setSelectedSection] = useState<'overview' | 'departments' | 'users' | 'hierarchy'>('overview');

  // Fetch company information
  const fetchCompanyInfo = useCallback(async () => {
    console.log('📡 CompanySettings - Fetching company info', { companyId, hasCompanyId: !!companyId });

    // Note: Backend uses session companyNumber if companyId is not provided
    try {
      setIsLoading(true);
      console.log('🚀 Calling getCompanySchema with companyId:', companyId);
      const response = await apis.getCompanySchema(companyId);
      console.log('✅ getCompanySchema response:', {
        hasResponse: !!response,
        hasData: !!response?.data,
        data: response?.data,
        departments: response?.data?.departments?.length
      });

      if (response?.data) {
        setCompanyInfo(response.data);
        setDepartments(response.data.departments || []);
        console.log('✅ Company info set successfully:', {
          companyName: response.data.company_name,
          departmentsCount: response.data.departments?.length
        });
      } else {
        console.warn('⚠️ No data in response:', response);
      }
    } catch (error) {
      console.error('❌ Error fetching company info:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      Alert.alert('Error', 'Failed to load company information');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  // Fetch user statistics
  const fetchUserStats = useCallback(async () => {
    console.log('📡 CompanySettings - Fetching user stats', { companyId, hasCompanyId: !!companyId });

    // Note: Backend primarily uses session companyNumber, but we pass companyId as fallback
    try {
      console.log('🚀 Calling retrieveCompanyUserStats with:', { companyId });
      const response = await apis.retrieveCompanyUserStats({ companyId });
      console.log('✅ retrieveCompanyUserStats response:', {
        hasResponse: !!response,
        totalUsers: response?.totalUsers,
        activeUsers: response?.activeUsers,
        departmentStats: response?.departmentStats?.length
      });

      if (response) {
        setUserStats(response);
        console.log('✅ User stats set successfully:', {
          totalUsers: response.totalUsers,
          activeUsers: response.activeUsers,
          departments: response.departmentStats?.length
        });
      } else {
        console.warn('⚠️ No response from retrieveCompanyUserStats');
      }
    } catch (error) {
      console.error('❌ Error fetching user stats:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
    }
  }, [companyId]);

  // Fetch company list
  const fetchCompanyList = useCallback(async () => {
    console.log('📡 CompanySettings - Fetching company list');
    try {
      const response = await apis.getCompanyList();
      console.log('✅ getCompanyList response:', {
        hasResponse: !!response,
        isArray: Array.isArray(response),
        count: response?.length
      });

      if (response) {
        setCompanyList(response);
        console.log('✅ Company list set successfully, count:', response.length);
      } else {
        console.warn('⚠️ No response from getCompanyList');
      }
    } catch (error) {
      console.error('❌ Error fetching company list:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
    }
  }, []);

  // Add new department
  const handleAddDepartment = async (values: any) => {
    console.log('🏢 Adding department:', values);

    // Backend uses session companyNumber, no need to check companyId
    try {
      setIsLoading(true);
      console.log('🚀 Calling addDepartment API with:', {
        companyId, // Optional - backend uses session
        department_name: values.departmentName,
        department_description: values.description
      });

      const response = await apis.addDepartment({
        companyId, // Optional - backend will use session companyNumber
        department_name: values.departmentName, // Schema expects department_name
        department_description: values.description // Schema expects department_description
      });

      console.log('✅ Department added response:', response);

      if (response?.success) {
        Alert.alert('Success', 'Department added successfully');
        // Refresh company info to show new department
        await fetchCompanyInfo();
      } else {
        console.error('❌ Department add failed:', response);
        Alert.alert('Error', response?.error || 'Failed to add department');
      }
    } catch (error) {
      console.error('❌ Error adding department:', {
        error,
        message: error?.message,
        response: error?.response?.data
      });
      Alert.alert('Error', error?.response?.data?.error || 'Failed to add department');
    } finally {
      setIsLoading(false);
    }
  };

  // Update company name
  const handleUpdateCompanyName = async (values: any) => {
    console.log('🏢 Updating company name:', values);

    // Backend uses session companyNumber, companyId is optional
    try {
      setIsLoading(true);
      console.log('🚀 Calling setCompanyName with:', { companyId, companyName: values.companyName });

      const response = await apis.setCompanyName(companyId, values.companyName);
      console.log('✅ Company name update response:', response);

      if (response?.success) {
        Alert.alert('Success', 'Company name updated successfully');
        // Refresh company info to show new name
        await fetchCompanyInfo();
      } else {
        console.error('❌ Company name update failed:', response);
        Alert.alert('Error', response?.error || 'Failed to update company name');
      }
    } catch (error) {
      console.error('❌ Error updating company name:', {
        error,
        message: error?.message,
        response: error?.response?.data
      });
      Alert.alert('Error', error?.response?.data?.error || 'Failed to update company name');
    } finally {
      setIsLoading(false);
    }
  };

  // Add company location
  const handleAddLocation = async (values: any) => {
    console.log('📍 Adding company location:', values);

    // Backend uses session companyNumber, companyId is optional
    try {
      setIsLoading(true);
      console.log('🚀 Calling addCompanyLocation with:', { companyId, location: values.location });

      const response = await apis.addCompanyLocation(companyId, values.location);
      console.log('✅ Location add response:', response);

      if (response?.success) {
        Alert.alert('Success', 'Location added successfully');
        // Refresh company info to show new location
        await fetchCompanyInfo();
      } else {
        console.error('❌ Location add failed:', response);
        Alert.alert('Error', response?.error || 'Failed to add location');
      }
    } catch (error) {
      console.error('❌ Error adding location:', {
        error,
        message: error?.message,
        response: error?.response?.data
      });
      Alert.alert('Error', error?.response?.data?.error || 'Failed to add location');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset hierarchy positions
  const handleResetPositions = async () => {
    console.log('🔄 Reset positions requested');

    Alert.alert(
      'Reset Positions',
      'Are you sure you want to reset all hierarchy positions? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: async () => {
            // Backend uses session companyNumber, companyId is optional
            try {
              setIsLoading(true);
              console.log('🚀 Calling resetCompanyPositions with:', { companyId });

              const response = await apis.resetCompanyPositions(companyId);
              console.log('✅ Reset positions response:', response);

              if (response?.success) {
                Alert.alert('Success', 'Hierarchy positions reset successfully');
                // Refresh company info
                await fetchCompanyInfo();
              } else {
                console.error('❌ Reset positions failed:', response);
                Alert.alert('Error', response?.error || 'Failed to reset positions');
              }
            } catch (error) {
              console.error('❌ Error resetting positions:', {
                error,
                message: error?.message,
                response: error?.response?.data
              });
              Alert.alert('Error', error?.response?.data?.error || 'Failed to reset positions');
            } finally {
              setIsLoading(false);
            }
          }
        }
      ]
    );
  };

  // Refresh data
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([
      fetchCompanyInfo(),
      fetchUserStats(),
      fetchCompanyList()
    ]);
    setRefreshing(false);
  }, [fetchCompanyInfo, fetchUserStats, fetchCompanyList]);

  // Initial data load
  useEffect(() => {
    console.log('🔄 CompanySettings - Initial data load triggered', {
      companyId,
      hasCompanyId: !!companyId
    });

    // Always fetch - backend uses session companyNumber if companyId not provided
    fetchCompanyInfo();
    fetchUserStats();
    fetchCompanyList();
  }, [companyId, fetchCompanyInfo, fetchUserStats, fetchCompanyList]);

  // Render section tabs
  const renderSectionTabs = () => (
    <Animatable.View animation="fadeInDown" duration={600}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ marginBottom: 20 }}
      >
        <XStack gap="$2">
          <Button
            backgroundColor={selectedSection === 'overview' ? (theme ? "#0ea5e9" : "#38bdf8") : (theme ? "#f1f5f9" : "#334155")}
            onPress={() => setSelectedSection('overview')}
            borderRadius={12}
            paddingHorizontal="$4"
            paddingVertical="$2"
          >
            <XStack alignItems="center" gap="$2">
              <MaterialIcons
                name="dashboard"
                size={18}
                color={selectedSection === 'overview' ? "white" : (theme ? "#64748b" : "#94a3b8")}
              />
              <SizableText
                color={selectedSection === 'overview' ? "white" : (theme ? "#64748b" : "#94a3b8")}
                fontWeight="600"
              >
                Overview
              </SizableText>
            </XStack>
          </Button>

          <Button
            backgroundColor={selectedSection === 'departments' ? (theme ? "#059669" : "#10b981") : (theme ? "#f1f5f9" : "#334155")}
            onPress={() => setSelectedSection('departments')}
            borderRadius={12}
            paddingHorizontal="$4"
            paddingVertical="$2"
          >
            <XStack alignItems="center" gap="$2">
              <MaterialIcons
                name="business"
                size={18}
                color={selectedSection === 'departments' ? "white" : (theme ? "#64748b" : "#94a3b8")}
              />
              <SizableText
                color={selectedSection === 'departments' ? "white" : (theme ? "#64748b" : "#94a3b8")}
                fontWeight="600"
              >
                Departments
              </SizableText>
            </XStack>
          </Button>

          <Button
            backgroundColor={selectedSection === 'users' ? (theme ? "#7c3aed" : "#a855f7") : (theme ? "#f1f5f9" : "#334155")}
            onPress={() => setSelectedSection('users')}
            borderRadius={12}
            paddingHorizontal="$4"
            paddingVertical="$2"
          >
            <XStack alignItems="center" gap="$2">
              <MaterialIcons
                name="people"
                size={18}
                color={selectedSection === 'users' ? "white" : (theme ? "#64748b" : "#94a3b8")}
              />
              <SizableText
                color={selectedSection === 'users' ? "white" : (theme ? "#64748b" : "#94a3b8")}
                fontWeight="600"
              >
                Users
              </SizableText>
            </XStack>
          </Button>

          <Button
            backgroundColor={selectedSection === 'hierarchy' ? (theme ? "#dc2626" : "#ef4444") : (theme ? "#f1f5f9" : "#334155")}
            onPress={() => setSelectedSection('hierarchy')}
            borderRadius={12}
            paddingHorizontal="$4"
            paddingVertical="$2"
          >
            <XStack alignItems="center" gap="$2">
              <MaterialIcons
                name="account-tree"
                size={18}
                color={selectedSection === 'hierarchy' ? "white" : (theme ? "#64748b" : "#94a3b8")}
              />
              <SizableText
                color={selectedSection === 'hierarchy' ? "white" : (theme ? "#64748b" : "#94a3b8")}
                fontWeight="600"
              >
                Hierarchy
              </SizableText>
            </XStack>
          </Button>
        </XStack>
      </ScrollView>
    </Animatable.View>
  );

  // Render overview section
  const renderOverviewSection = () => (
    <YStack gap="$4">
      {/* Company Info Card */}
      <Animatable.View animation="fadeInUp" delay={100} duration={600}>
        <Card
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$5"
          elevate
        >
          <XStack alignItems="center" gap="$3" marginBottom="$4">
            <View style={{
              backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
              borderRadius: 12,
              padding: 12
            }}>
              <MaterialIcons
                name="business"
                size={28}
                color={theme ? "#0ea5e9" : "#38bdf8"}
              />
            </View>
            <YStack flex={1}>
              <H3 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                {companyInfo?.company_name || 'Company Information'}
              </H3>
              <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$3">
                {companyInfo?.location || 'No location set'}
              </SizableText>
            </YStack>
          </XStack>

          <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$3" />

          <YStack gap="$3">
            <XStack justifyContent="space-between" alignItems="center">
              <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$4">
                Total Departments
              </SizableText>
              <SizableText color={theme ? "#1e293b" : "#f1f5f9"} size="$5" fontWeight="600">
                {departments.length}
              </SizableText>
            </XStack>

            <XStack justifyContent="space-between" alignItems="center">
              <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$4">
                Total Users
              </SizableText>
              <SizableText color={theme ? "#1e293b" : "#f1f5f9"} size="$5" fontWeight="600">
                {userStats?.totalUsers || 0}
              </SizableText>
            </XStack>

            <XStack justifyContent="space-between" alignItems="center">
              <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$4">
                Active Users
              </SizableText>
              <SizableText color={theme ? "#10b981" : "#34d399"} size="$5" fontWeight="600">
                {userStats?.activeUsers || 0}
              </SizableText>
            </XStack>
          </YStack>

          <XStack gap="$2" marginTop="$4" flexWrap="wrap">
            <CustomDialog
              title="Update Company Name"
              confirmText="Update"
              cancelText="Cancel"
              schema={[
                {
                  type: 'text',
                  title: 'Company Name',
                  ref: 'companyName',
                  content: {
                    placeholder: 'Enter company name...',
                    defaultValue: companyInfo?.company_name
                  }
                }
              ]}
              onPress={handleUpdateCompanyName}
              trigger={
                <Button
                  flex={1}
                  backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                  color="white"
                  size="$3"
                  borderRadius={12}
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="edit" size={16} color="white" />
                    <SizableText color="white" size="$3">Edit Name</SizableText>
                  </XStack>
                </Button>
              }
            />

            <CustomDialog
              title="Add Location"
              confirmText="Add"
              cancelText="Cancel"
              schema={[
                {
                  type: 'text',
                  title: 'Location',
                  ref: 'location',
                  content: {
                    placeholder: 'Enter location...',
                    defaultValue: companyInfo?.location
                  }
                }
              ]}
              onPress={handleAddLocation}
              trigger={
                <Button
                  flex={1}
                  backgroundColor={theme ? "#059669" : "#10b981"}
                  color="white"
                  size="$3"
                  borderRadius={12}
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="place" size={16} color="white" />
                    <SizableText color="white" size="$3">Location</SizableText>
                  </XStack>
                </Button>
              }
            />
          </XStack>
        </Card>
      </Animatable.View>

      {/* Quick Stats */}
      <Animatable.View animation="fadeInUp" delay={200} duration={600}>
        <XStack gap="$3" flexWrap="wrap">
          <Card
            flex={1}
            minWidth={150}
            backgroundColor={theme ? "#f0f9ff" : "#0c4a6e"}
            borderColor={theme ? "#e0f2fe" : "#075985"}
            borderRadius={16}
            padding="$4"
          >
            <YStack alignItems="center" gap="$2">
              <MaterialIcons
                name="groups"
                size={32}
                color={theme ? "#0ea5e9" : "#38bdf8"}
              />
              <SizableText
                size="$6"
                color={theme ? "#0ea5e9" : "#38bdf8"}
                fontWeight="700"
              >
                {userStats?.totalUsers || 0}
              </SizableText>
              <SizableText
                size="$3"
                color={theme ? "#0369a1" : "#7dd3fc"}
                textAlign="center"
              >
                Total Users
              </SizableText>
            </YStack>
          </Card>

          <Card
            flex={1}
            minWidth={150}
            backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
            borderColor={theme ? "#dcfce7" : "#065f46"}
            borderRadius={16}
            padding="$4"
          >
            <YStack alignItems="center" gap="$2">
              <MaterialIcons
                name="business-center"
                size={32}
                color={theme ? "#059669" : "#10b981"}
              />
              <SizableText
                size="$6"
                color={theme ? "#059669" : "#10b981"}
                fontWeight="700"
              >
                {departments.length}
              </SizableText>
              <SizableText
                size="$3"
                color={theme ? "#047857" : "#6ee7b7"}
                textAlign="center"
              >
                Departments
              </SizableText>
            </YStack>
          </Card>
        </XStack>
      </Animatable.View>
    </YStack>
  );

  // Render departments section
  const renderDepartmentsSection = () => (
    <YStack gap="$4">
      <Animatable.View animation="fadeInUp" delay={100} duration={600}>
        <XStack alignItems="center" justifyContent="space-between" marginBottom="$3">
          <H3 color={theme ? "#1e293b" : "#f1f5f9"}>Departments</H3>
          <CustomDialog
            title="Add Department"
            confirmText="Add"
            cancelText="Cancel"
            schema={[
              {
                type: 'text',
                title: 'Department Name',
                ref: 'departmentName',
                content: {
                  placeholder: 'Enter department name...'
                }
              },
              {
                type: 'text',
                title: 'Description',
                ref: 'description',
                content: {
                  placeholder: 'Enter description (optional)...'
                }
              }
            ]}
            onPress={handleAddDepartment}
            trigger={
              <Button
                backgroundColor={theme ? "#059669" : "#10b981"}
                color="white"
                size="$3"
                borderRadius={12}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="add" size={18} color="white" />
                  <SizableText color="white" size="$3">Add New</SizableText>
                </XStack>
              </Button>
            }
          />
        </XStack>
      </Animatable.View>

      {departments.length > 0 ? (
        departments.map((dept, index) => (
          <Animatable.View
            key={dept._id || index}
            animation="fadeInUp"
            delay={100 + (index * 50)}
            duration={600}
          >
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$4"
            >
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#d1fae5" : "#064e3b",
                  borderRadius: 12,
                  padding: 12
                }}>
                  <MaterialIcons
                    name="business"
                    size={24}
                    color={theme ? "#059669" : "#10b981"}
                  />
                </View>
                <YStack flex={1}>
                  <SizableText
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    size="$5"
                    fontWeight="600"
                  >
                    {dept.name}
                  </SizableText>
                  {dept.description && (
                    <SizableText
                      color={theme ? "#64748b" : "#94a3b8"}
                      size="$3"
                    >
                      {dept.description}
                    </SizableText>
                  )}
                </YStack>
                <Button
                  backgroundColor="transparent"
                  padding="$2"
                  onPress={() => Alert.alert('Department', `Manage ${dept.name}`)}
                >
                  <MaterialIcons
                    name="more-vert"
                    size={24}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                </Button>
              </XStack>
            </Card>
          </Animatable.View>
        ))
      ) : (
        <Animatable.View animation="fadeInUp" delay={200} duration={600}>
          <Card
            backgroundColor={theme ? "#f8fafc" : "#1e293b"}
            borderColor={theme ? "#e2e8f0" : "#334155"}
            borderRadius={16}
            padding="$6"
          >
            <YStack alignItems="center" gap="$3">
              <MaterialIcons
                name="business-center"
                size={48}
                color={theme ? "#cbd5e1" : "#475569"}
              />
              <SizableText
                color={theme ? "#64748b" : "#94a3b8"}
                size="$4"
                textAlign="center"
              >
                No departments yet.{'\n'}Add your first department to get started!
              </SizableText>
            </YStack>
          </Card>
        </Animatable.View>
      )}
    </YStack>
  );

  // Render users section
  const renderUsersSection = () => (
    <YStack gap="$4">
      <Animatable.View animation="fadeInUp" delay={100} duration={600}>
        <Card
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$5"
          elevate
        >
          <XStack alignItems="center" gap="$3" marginBottom="$4">
            <MaterialIcons
              name="people"
              size={28}
              color={theme ? "#7c3aed" : "#a855f7"}
            />
            <H3 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
              User Statistics
            </H3>
          </XStack>

          <YStack gap="$3">
            <XStack
              backgroundColor={theme ? "#f0f9ff" : "#0c4a6e"}
              padding="$3"
              borderRadius={12}
              justifyContent="space-between"
              alignItems="center"
            >
              <SizableText color={theme ? "#0369a1" : "#7dd3fc"} size="$4" fontWeight="600">
                Total Users
              </SizableText>
              <SizableText color={theme ? "#0ea5e9" : "#38bdf8"} size="$6" fontWeight="700">
                {userStats?.totalUsers || 0}
              </SizableText>
            </XStack>

            <XStack
              backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
              padding="$3"
              borderRadius={12}
              justifyContent="space-between"
              alignItems="center"
            >
              <SizableText color={theme ? "#047857" : "#6ee7b7"} size="$4" fontWeight="600">
                Active Users
              </SizableText>
              <SizableText color={theme ? "#059669" : "#10b981"} size="$6" fontWeight="700">
                {userStats?.activeUsers || 0}
              </SizableText>
            </XStack>
          </YStack>

          <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$4" />

          <SizableText
            color={theme ? "#1e293b" : "#f1f5f9"}
            size="$4"
            fontWeight="600"
            marginBottom="$3"
          >
            Department Distribution
          </SizableText>

          {userStats?.departmentStats && userStats.departmentStats.length > 0 ? (
            <YStack gap="$2">
              {userStats.departmentStats.map((dept, index) => (
                <XStack
                  key={dept.departmentId || index}
                  alignItems="center"
                  padding="$3"
                  backgroundColor={theme ? "#f1f5f9" : "#334155"}
                  borderRadius={8}
                  justifyContent="space-between"
                >
                  <SizableText color={theme ? "#1e293b" : "#f1f5f9"} size="$3">
                    {dept.departmentName}
                  </SizableText>
                  <View style={{
                    backgroundColor: theme ? "#7c3aed" : "#a855f7",
                    borderRadius: 12,
                    paddingHorizontal: 12,
                    paddingVertical: 4
                  }}>
                    <SizableText color="white" size="$3" fontWeight="600">
                      {dept.userCount}
                    </SizableText>
                  </View>
                </XStack>
              ))}
            </YStack>
          ) : (
            <SizableText
              color={theme ? "#64748b" : "#94a3b8"}
              size="$3"
              textAlign="center"
              paddingVertical="$4"
            >
              No department statistics available
            </SizableText>
          )}
        </Card>
      </Animatable.View>
    </YStack>
  );

  // Render hierarchy section
  const renderHierarchySection = () => (
    <YStack gap="$4">
      <Animatable.View animation="fadeInUp" delay={100} duration={600}>
        <Card
          backgroundColor={theme ? "#f8fafc" : "#1e293b"}
          borderColor={theme ? "#e2e8f0" : "#334155"}
          borderRadius={16}
          padding="$5"
          elevate
        >
          <XStack alignItems="center" gap="$3" marginBottom="$4">
            <View style={{
              backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
              borderRadius: 12,
              padding: 12
            }}>
              <MaterialIcons
                name="account-tree"
                size={28}
                color={theme ? "#dc2626" : "#ef4444"}
              />
            </View>
            <YStack flex={1}>
              <H3 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                Hierarchy Management
              </H3>
              <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$3">
                Manage organizational structure
              </SizableText>
            </YStack>
          </XStack>

          <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$3" />

          <YStack gap="$3">
            <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$4">
              Manage your company's organizational hierarchy, assign positions, and reset the structure if needed.
            </SizableText>

            <XStack gap="$3" flexWrap="wrap" marginTop="$3">
              <Button
                flex={1}
                minWidth={140}
                backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                color="white"
                borderRadius={12}
                onPress={() => Alert.alert('Build Link', 'Build hierarchy link feature coming soon')}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="link" size={18} color="white" />
                  <SizableText color="white" size="$3">Build Link</SizableText>
                </XStack>
              </Button>

              <Button
                flex={1}
                minWidth={140}
                backgroundColor={theme ? "#dc2626" : "#ef4444"}
                color="white"
                borderRadius={12}
                onPress={handleResetPositions}
                disabled={isLoading}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="refresh" size={18} color="white" />
                  <SizableText color="white" size="$3">Reset Positions</SizableText>
                </XStack>
              </Button>
            </XStack>
          </YStack>
        </Card>
      </Animatable.View>

      <Animatable.View animation="fadeInUp" delay={200} duration={600}>
        <Card
          backgroundColor={theme ? "#fef3c7" : "#451a03"}
          borderColor={theme ? "#f59e0b" : "#d97706"}
          borderRadius={16}
          padding="$4"
        >
          <XStack alignItems="center" gap="$3" marginBottom="$3">
            <MaterialIcons
              name="warning"
              size={24}
              color={theme ? "#d97706" : "#f59e0b"}
            />
            <H4 color={theme ? "#d97706" : "#f59e0b"} fontWeight="600">
              Warning
            </H4>
          </XStack>
          <SizableText color={theme ? "#92400e" : "#fbbf24"} size="$3">
            Resetting positions will clear all organizational hierarchy data. This action cannot be undone. Please ensure you have a backup if needed.
          </SizableText>
        </Card>
      </Animatable.View>
    </YStack>
  );

  // Main render
  return (
    <DefaultScreen noMargin>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 16 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={theme ? "#0ea5e9" : "#38bdf8"}
          />
        }
      >
        <YStack gap="$4">
          {/* Header */}
          <Animatable.View animation="fadeInDown" duration={600}>
            <XStack alignItems="center" marginBottom="$4">
              <View style={{
                backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                borderRadius: 16,
                padding: 16,
                marginRight: 16
              }}>
                <MaterialIcons
                  name="settings"
                  size={32}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                />
              </View>
              <YStack flex={1}>
                <H6
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  fontWeight="700"
                >
                  Company Settings
                </H6>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  marginTop="$1"
                >
                  Manage your company information and structure
                </SizableText>
              </YStack>
            </XStack>
          </Animatable.View>

          {/* Section Tabs */}
          {renderSectionTabs()}

          {/* Loading State */}
          {isLoading && !refreshing && (
            <YStack alignItems="center" paddingVertical="$6">
              <Spinner size="large" color={theme ? "#0ea5e9" : "#38bdf8"} />
            </YStack>
          )}

          {/* Content Sections */}
          {!isLoading && (
            <>
              {selectedSection === 'overview' && renderOverviewSection()}
              {selectedSection === 'departments' && renderDepartmentsSection()}
              {selectedSection === 'users' && renderUsersSection()}
              {selectedSection === 'hierarchy' && renderHierarchySection()}
            </>
          )}
        </YStack>
      </ScrollView>
    </DefaultScreen>
  );
};

export default CompanySettings;
